import { computed, shallowRef, watch, type Ref } from 'vue';

/**
 * Composable для сохранения предыдущих данных Apollo Query во время загрузки новых
 *
 * Решает проблему Apollo Client, который сбрасывает result при изменении переменных запроса.
 * Сохраняет предыдущие данные, чтобы UI оставался стабильным во время загрузки.
 *
 * @param result - Ref с результатом Apollo Query
 * @param loading - Ref с состоянием загрузки
 * @returns Объект с displayData, isInitialLoading и isFetching
 *
 * @example
 * ```typescript
 * const { result, loading } = useGetClientsQuery({ searchQuery });
 *
 * const { displayData, isInitialLoading, isFetching } = useApolloQueryWithPreviousData(result, loading);
 *
 * const clients = computed(() => displayData.value?.clients ?? []);
 * ```
 */
export function useApolloQueryWithPreviousData<TResult>(result: Ref<TResult | undefined>, loading: Ref<boolean>) {
  // Сохраняем предыдущие успешно загруженные данные
  const previousData = shallowRef<TResult | null>(result.value ?? null);

  // Обновляем previousData при получении новых данных
  watch(
    result,
    (newResult) => {
      if (newResult !== undefined) {
        previousData.value = newResult;
      }
    },
    { flush: 'sync' },
  );

  /**
   * Данные для отображения: текущие или предыдущие (если идет загрузка)
   */
  const displayData = computed<TResult | null>(() => {
    return (result.value as TResult) ?? previousData.value;
  });

  /**
   * Начальная загрузка (skeleton) - когда нет предыдущих данных
   */
  const isInitialLoading = computed(() => loading.value && displayData.value === null);

  /**
   * Фоновая загрузка (spinner) - когда есть предыдущие данные
   */
  const isFetching = computed(() => loading.value && displayData.value !== null);

  return {
    displayData,
    isInitialLoading,
    isFetching,
  };
}
